import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, Info, CheckCheck, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { systemUpdatesApi, SystemUpdate } from '../domains/admin/api/systemUpdates';

interface Notification {
  id: number;
  userId: number;
  companyId?: number;
  type: 'info' | 'warning' | 'error' | 'success' | 'compliance' | 'system' | 'security';
  title: string;
  message: string;
  data?: any;
  isRead: boolean;
  readAt?: Date;
  createdAt: string;
  scheduledFor?: Date;
  sentAt?: Date;
  deliveryMethod: 'email' | 'sms' | 'push' | 'in_app' | 'webhook';
  metadata?: any;
}

const API_BASE = '/api/notification';

const NotificationBell: React.FC = () => {
  const { user, hasPermission, userDomain } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [systemUpdates, setSystemUpdates] = useState<SystemUpdate[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [systemUpdateCount, setSystemUpdateCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isMarkingAll, setIsMarkingAll] = useState(false);
  const sseRef = useRef<EventSource | null>(null);

  // ---- SSE real-time connection -------------------------------------------
  useEffect(() => {
    if (!user) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    const connectSSE = () => {
      // Close any existing connection
      sseRef.current?.close();

      const es = new EventSource(`${API_BASE}/stream?token=${encodeURIComponent(token)}`);
      sseRef.current = es;

      es.addEventListener('connected', () => {
        // SSE handshake confirmed — no action needed
      });

      es.addEventListener('notification', (e: MessageEvent) => {
        try {
          const incoming = JSON.parse(e.data) as Partial<Notification> & { event?: string };
          const newNotif: Notification = {
            id: incoming.id!,
            userId: user.id,
            type: (incoming.type as Notification['type']) || 'info',
            title: incoming.title || '',
            message: incoming.message || '',
            isRead: false,
            deliveryMethod: (incoming.deliveryMethod as Notification['deliveryMethod']) || 'in_app',
            createdAt: incoming.createdAt || new Date().toISOString(),
            data: incoming.data
          };
          setNotifications(prev => [newNotif, ...prev]);
          setUnreadCount(prev => prev + 1);
        } catch {
          // Malformed SSE payload — ignore
        }
      });

      // Dedicated new_message event — triggers toast popup
      es.addEventListener('new_message', (e: MessageEvent) => {
        try {
          const data = JSON.parse(e.data);
          window.dispatchEvent(new CustomEvent('newMessageReceived', { detail: data }));
        } catch {
          // Malformed payload — ignore
        }
      });

      es.onerror = () => {
        es.close();
        sseRef.current = null;
        // Reconnect after 10s if user is still logged in
        setTimeout(connectSSE, 10000);
      };
    };

    connectSSE();
    return () => {
      sseRef.current?.close();
      sseRef.current = null;
    };
  }, [user]);

  // ---- Initial load -------------------------------------------------------
  useEffect(() => {
    if (!user) return;
    loadNotifications();
    loadSystemUpdates();
  }, [user]);

  // ---- Polling fallback every 60 seconds (backstop for missed SSE events) --
  useEffect(() => {
    if (!user) return;
    const pollInterval = setInterval(() => {
      loadNotifications(1, false);
      loadSystemUpdates();
    }, 60000);
    return () => clearInterval(pollInterval);
  }, [user]);

  // ---- Window-level custom events -----------------------------------------
  useEffect(() => {
    if (!user) return;
    const onChangeNotif = () => loadNotifications(1, false);
    const onSystemDismiss = () => loadSystemUpdates();
    window.addEventListener('changeNotificationReceived', onChangeNotif);
    window.addEventListener('systemUpdatesDismissed', onSystemDismiss);
    return () => {
      window.removeEventListener('changeNotificationReceived', onChangeNotif);
      window.removeEventListener('systemUpdatesDismissed', onSystemDismiss);
    };
  }, [user]);

  // ---- API helpers --------------------------------------------------------
  const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? null;

  const authHeaders = () => {
    const csrf = getCsrfToken();
    return {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
      ...(csrf ? { 'X-CSRF-Token': csrf } : {})
    };
  };

  const loadSystemUpdates = async () => {
    if (!user) return;
    try {
      const response = await systemUpdatesApi.getUnseenUpdates();
      if (response.success) {
        setSystemUpdates(response.updates);
        setSystemUpdateCount(response.unseen_count);
      } else {
        setSystemUpdates([]);
        setSystemUpdateCount(0);
      }
    } catch {
      setSystemUpdates([]);
      setSystemUpdateCount(0);
    }
  };

  const loadNotifications = async (page: number = 1, append: boolean = false) => {
    if (!user) return;
    if (page === 1) setIsLoading(true);
    else setIsLoadingMore(true);

    try {
      const limit = 10;
      const offset = (page - 1) * limit;
      const response = await fetch(`${API_BASE}?limit=${limit}&offset=${offset}`, {
        headers: authHeaders()
      });

      let notifs: Notification[] = [];
      if (response.ok) {
        const result = await response.json();
        notifs = result.data || [];
      } else if (response.status !== 404) {
        throw new Error(`HTTP ${response.status}`);
      }

      if (append) {
        setNotifications(prev => [...prev, ...notifs]);
      } else {
        setNotifications(notifs);
        setCurrentPage(1);
      }
      setHasMore(notifs.length === limit);

      // Refresh unread count on first page
      if (page === 1) {
        try {
          const countRes = await fetch(`${API_BASE}/unread-count`, { headers: authHeaders() });
          if (countRes.ok) {
            const countData = await countRes.json();
            setUnreadCount(countData.count ?? countData.data?.count ?? countData.data?.unreadCount ?? 0);
          }
        } catch {
          setUnreadCount(notifs.filter(n => !n.isRead).length);
        }
      }
    } catch {
      // Silently fail — don't disrupt the app for a failed poll
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const loadMoreNotifications = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    loadNotifications(nextPage, true);
  };

  const markAsRead = async (notificationId: number) => {
    // Optimistic update
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      const res = await fetch(`${API_BASE}/${notificationId}/read`, {
        method: 'PUT',
        headers: authHeaders()
      });
      if (!res.ok) {
        // Revert
        setNotifications(prev =>
          prev.map(n => n.id === notificationId ? { ...n, isRead: false } : n)
        );
        setUnreadCount(prev => prev + 1);
      }
    } catch {
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, isRead: false } : n)
      );
      setUnreadCount(prev => prev + 1);
    }
  };

  const markAllAsRead = async () => {
    if (isMarkingAll || unreadCount === 0) return;
    setIsMarkingAll(true);

    // Optimistic update
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnreadCount(0);

    try {
      await fetch(`${API_BASE}/read-all`, { method: 'PUT', headers: authHeaders() });
    } catch {
      // Refresh to get accurate state
      loadNotifications(1, false);
    } finally {
      setIsMarkingAll(false);
    }
  };

  const deleteNotification = async (e: React.MouseEvent, notificationId: number) => {
    e.stopPropagation();

    // Optimistic removal
    const removed = notifications.find(n => n.id === notificationId);
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    if (removed && !removed.isRead) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }

    try {
      const res = await fetch(`${API_BASE}/${notificationId}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (!res.ok && removed) {
        // Revert
        setNotifications(prev => [removed, ...prev].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        ));
        if (!removed.isRead) setUnreadCount(prev => prev + 1);
      }
    } catch {
      if (removed) {
        setNotifications(prev => [removed, ...prev].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        ));
        if (!removed.isRead) setUnreadCount(prev => prev + 1);
      }
    }
  };

  // ---- Helpers ------------------------------------------------------------
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'error':      return 'text-red-600 bg-red-50';
      case 'warning':    return 'text-amber-600 bg-amber-50';
      case 'success':    return 'text-emerald-600 bg-emerald-50';
      case 'compliance': return 'text-blue-600 bg-blue-50';
      case 'system':     return 'text-purple-600 bg-purple-50';
      case 'security':   return 'text-orange-600 bg-orange-50';
      default:           return 'text-slate-600 bg-slate-50';
    }
  };

  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'error':      return 'bg-red-100 text-red-700';
      case 'warning':    return 'bg-amber-100 text-amber-700';
      case 'success':    return 'bg-emerald-100 text-emerald-700';
      case 'compliance': return 'bg-blue-100 text-blue-700';
      case 'system':     return 'bg-purple-100 text-purple-700';
      case 'security':   return 'bg-orange-100 text-orange-700';
      default:           return 'bg-slate-100 text-slate-700';
    }
  };

  const formatTimestamp = (timestamp: string | null | undefined) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '';
      const diffMs = Date.now() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      if (diffMins < 1)  return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7)  return `${diffDays}d ago`;
      return date.toLocaleDateString();
    } catch {
      return '';
    }
  };

  const totalBadgeCount = unreadCount + systemUpdateCount;

  // Combined & sorted items for display
  const combinedItems = [
    ...notifications.slice(0, 10).map(n => ({ ...n, itemType: 'notification' as const })),
    ...systemUpdates.slice(0, 5).map(s => ({
      id: `system-${s.id}` as any,
      type: 'system' as const,
      title: s.title,
      message: s.summary,
      createdAt: s.created_at,
      isRead: s.dismissed,
      deliveryMethod: 'in_app' as const,
      itemType: 'system' as const,
      systemUpdate: s
    }))
  ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
   .slice(0, 15);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        title="Notifications"
      >
        <Bell size={20} />
        {totalBadgeCount > 0 && (
          <span className={`absolute -top-1 -right-1 h-5 w-5 text-white text-xs font-medium rounded-full flex items-center justify-center ${
            systemUpdateCount > 0 ? 'bg-orange-500' : 'bg-red-500'
          }`}>
            {totalBadgeCount > 99 ? '99+' : totalBadgeCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />

          <div className="absolute right-0 top-full mt-2 w-96 bg-white border border-slate-200 rounded-xl shadow-xl z-[9999] max-h-[32rem] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 shrink-0">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Notifications</h3>
                <p className="text-xs text-slate-600">
                  {unreadCount > 0
                    ? `${unreadCount} unread${systemUpdates.length > 0 ? ` · ${systemUpdates.length} system update${systemUpdates.length > 1 ? 's' : ''}` : ''}`
                    : 'All caught up'}
                </p>
              </div>
              <div className="flex items-center gap-1">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    disabled={isMarkingAll}
                    className="p-1.5 text-slate-400 hover:text-blue-600 rounded-lg hover:bg-white/60 transition-colors"
                    title="Mark all as read"
                  >
                    <CheckCheck size={16} />
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-white/60 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* List */}
            <div className="overflow-y-auto flex-1">
              {isLoading ? (
                <div className="p-6 text-center text-slate-500 text-sm">Loading notifications...</div>
              ) : combinedItems.length === 0 ? (
                <div className="p-10 text-center">
                  <Bell className="mx-auto text-slate-300 mb-3" size={36} />
                  <p className="text-slate-500 text-sm font-medium">No notifications yet</p>
                  <p className="text-slate-400 text-xs mt-1">You're all caught up!</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {/* Category summary bar */}
                  {(() => {
                    const cats: Record<string, number> = {};
                    combinedItems.forEach(item => {
                      const cat = item.itemType === 'system' ? 'system' : (item.type || 'info');
                      cats[cat] = (cats[cat] || 0) + 1;
                    });
                    return Object.keys(cats).length > 1 ? (
                      <div className="px-4 py-2 bg-slate-50 flex flex-wrap gap-1.5 border-b border-slate-100">
                        {Object.entries(cats).map(([cat, count]) => (
                          <span key={cat} className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${getTypeBadgeColor(cat)}`}>
                            {cat} ({count})
                          </span>
                        ))}
                      </div>
                    ) : null;
                  })()}
                  {combinedItems.map((item) => (
                    <div
                      key={item.id}
                      className={`p-4 hover:bg-slate-50 cursor-pointer transition-all duration-150 border-l-4 group ${
                        !item.isRead
                          ? 'bg-blue-50/30 border-l-blue-500'
                          : 'bg-white border-l-transparent'
                      }`}
                      onClick={async () => {
                        if (item.itemType === 'notification') {
                          if (!item.isRead) {
                            await markAsRead(item.id);
                          }
                          // Honor an explicit deepLink on the notification
                          // payload — release notes, feature pings, page
                          // comments, etc. all set one. Fall back to the
                          // messages inbox for message-type notifications,
                          // which is the legacy default.
                          let data: any = (item as any).data;
                          if (typeof data === 'string') {
                            try { data = JSON.parse(data); } catch { data = null; }
                          }
                          const deepLink: string | undefined = data?.deepLink;
                          if (deepLink) {
                            navigate(deepLink);
                          } else if (data?.messageId) {
                            const path = userDomain === 'admin'
                              ? '/admin/messages'
                              : userDomain === 'company'
                                ? '/company/messages'
                                : userDomain === 'audit'
                                  ? '/audit/messages'
                                  : '/compliance/messages';
                            navigate(`${path}?id=${data.messageId}`);
                          } else {
                            const path = userDomain === 'admin'
                              ? '/admin/messages'
                              : userDomain === 'company'
                                ? '/company/messages'
                                : userDomain === 'audit'
                                  ? '/audit/messages'
                                  : '/compliance/messages';
                            navigate(path);
                          }
                          setIsOpen(false);
                        } else if (item.itemType === 'system' && !item.isRead && (item as any).systemUpdate) {
                          const su = (item as any).systemUpdate;
                          setSystemUpdates(prev => prev.filter(s => s.id !== su.id));
                          setSystemUpdateCount(prev => Math.max(0, prev - 1));
                          try {
                            await systemUpdatesApi.dismissUpdate(su.id);
                          } catch {
                            loadSystemUpdates();
                          }
                        }
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg shadow-sm flex-shrink-0 ${getTypeColor(item.type)}`}>
                          {item.itemType === 'system' ? <Info size={14} /> : <Bell size={14} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <h4 className={`text-sm font-medium truncate ${
                              !item.isRead ? 'text-slate-900' : 'text-slate-700'
                            }`}>
                              {item.title}
                            </h4>
                            {!item.isRead && (
                              <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 animate-pulse" />
                            )}
                          </div>
                          <p className="text-xs text-slate-600 line-clamp-2 mb-2">{item.message}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-400">{formatTimestamp(item.createdAt)}</span>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide ${
                              item.itemType === 'system' ? 'bg-purple-100 text-purple-700' : getTypeBadgeColor(item.type)
                            }`}>
                              {item.itemType === 'system' ? 'System' : item.type}
                            </span>
                          </div>
                        </div>
                        {item.itemType === 'notification' && (
                          <button
                            onClick={(e) => deleteNotification(e, item.id)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-slate-300 hover:text-red-500 rounded flex-shrink-0"
                            title="Delete notification"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-slate-100 text-center shrink-0 flex items-center justify-between">
              {hasMore && !isLoading ? (
                <button
                  onClick={loadMoreNotifications}
                  disabled={isLoadingMore}
                  className="text-xs text-blue-600 hover:text-blue-700 font-semibold disabled:opacity-50"
                >
                  {isLoadingMore ? 'Loading...' : 'Load more'}
                </button>
              ) : <span />}
              <button
                onClick={() => {
                  const path = userDomain === 'admin' ? '/admin/messages' : userDomain === 'company' ? '/company/notifications' : '/compliance/messages';
                  navigate(path);
                  setIsOpen(false);
                }}
                className="text-xs text-indigo-600 hover:text-indigo-700 font-semibold"
              >
                View All Notifications &rarr;
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
